import { Box, Typography, useTheme } from '@mui/material';
import {
  TrendingUp as ChartLineIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
  BarChart as ChartBarIcon,
  ExpandMore as ChevronDownIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Modal from '@/components/Modals/Modal';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

const HelpModal = ({ open, onClose }: HelpModalProps) => {
  const theme = useTheme();

  return (
    <Modal open={open} onClose={onClose} title="Insights Help" maxWidth={700}>
      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Report Types */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ChartLineIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Report Types
            </Typography>
          </Box>
          <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 1.5, color: theme.palette.text.secondary } }}>
            <li>
              <strong>Campaign Report:</strong> Shows aggregated performance metrics grouped by campaign
            </li>
            <li>
              <strong>Ad Report:</strong> Shows performance metrics at the ad level with campaign, adset, and ad IDs
            </li>
          </Box>
        </Box>

        {/* Group by Date */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CalendarIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Group by Date
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Enable this option to see daily breakdown for each campaign/ad, showing one row per date. Useful for analyzing performance trends over time.
          </Typography>
        </Box>

        {/* Filtering */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtering
            </Typography>
          </Box>
          <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 1.5, color: theme.palette.text.secondary } }}>
            <li>
              <strong>Column Filters:</strong> Type directly in the input boxes below column headers to filter
            </li>
            <li>
              <strong>Numeric Filters:</strong> Use operators like{' '}
              <Box component="code" sx={{ px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                &gt;100
              </Box>
              ,{' '}
              <Box component="code" sx={{ px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                &lt;50
              </Box>
              ,{' '}
              <Box component="code" sx={{ px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                10..20
              </Box>{' '}
              for ranges
            </li>
            <li>
              <strong>Bulk Filters:</strong> Paste multiple IDs (comma-separated) to filter by specific campaigns, adsets, or ads
            </li>
          </Box>
        </Box>

        {/* Copy IDs */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CopyIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Copy IDs
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Hover over any Campaign ID, Ad ID, or Adset ID to reveal a copy icon. Click to copy the ID to your clipboard.
          </Typography>
        </Box>

        {/* Trend Charts */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ChartBarIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Trend Charts
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            When not grouped by date, ROAS and RPC trend charts show mini bar graphs comparing first half vs second half of the date range. Hover over bars to see exact values.
          </Typography>
        </Box>

        {/* Daily Breakdown */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ChevronDownIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Daily Breakdown
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            In Campaign Report (without "Group by Date"), click the chevron next to campaign names to expand and see daily performance breakdown.
          </Typography>
        </Box>

        {/* Export */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DownloadIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Export
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Click "Export CSV" to download the currently filtered data as a CSV file for further analysis in Excel or Google Sheets.
          </Typography>
        </Box>

        {/* Metrics Explained */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Metrics Explained
            </Typography>
          </Box>
          <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 1, color: theme.palette.text.secondary, fontSize: '0.875rem' } }}>
            <li>
              <strong>P&L:</strong> Profit and Loss (Revenue - Spend)
            </li>
            <li>
              <strong>ROAS:</strong> Return on Ad Spend (Revenue / Spend)
            </li>
            <li>
              <strong>AVG RPC:</strong> Average Revenue Per Click (Revenue / Clicks)
            </li>
            <li>
              <strong>Cost/Purchase:</strong> Cost Per Purchase (Spend / Purchases)
            </li>
            <li>
              <strong>Cost/Click:</strong> Cost Per Link Click (Spend / Link Clicks)
            </li>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default HelpModal;
